import { DateTime } from 'luxon';

import { NewPatient } from './patients';
import { buildQueryString } from '@/utils/utils';

export type Booking = {
  id: number;
  userId: string;
  agendaConfigurationId: number;
  calendarEventId: string;
  recurrenceId?: number;
  name: string;
  phone: string;
  email: string;
  startDate: Date;
  duration: number;
  meetingUrl: string;
  completed: boolean;
  paid: boolean;
  configuration: number;
  paymentAmount: number;
  paymentCurrency: string;
  paymentMethod: string | null;
  patientId: number | null;
  comments: string;
  bookingUrl: string;
  psychologistUrl?: string;
  timeZone?: string;
  stripeChargeId?: string;
  stripeChargeLink?: string;
  stripePaymentLink?: string;
  locationDetails: string;
  notes: string | null;
  status: number;
  bookingTags: string[];
};

export type BookingResponse = {
  id: number;
  name: string;
  phone: string;
  email: string;
  startDate: Date;
  duration: number;
  meetingUrl: string;
  completed: boolean;
  paid: boolean;
  configuration: number;
  paymentAmount: number;
  paymentCurrency: string;
  patientId: number | null;
  comments: string;
  bookingUrl: string;
  timeZone?: string;
};

export type CreateBooking = {
  agendaConfigurationId: number;
  onSite: boolean;
  name: string;
  phone: string;
  email: string;
  startDate: Date;
  patientId: number | null;
  comments: string;
};

type BookingSearchParams = {
  page?: number;
  limit?: number;
  startDate?: Date;
  endDate?: Date;
  name?: string | null | undefined;
  email?: string | null | undefined;
  phone?: string | null | undefined;
  completed?: string;
  userId?: string | null;
  extension?: string | null;
  paid?: string;
  orderBy?: string;
  orderDir?: boolean;
  paginate?: boolean;
};

type BookingSearchResponse = {
  bookings: Booking[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
  };
};

export interface BookingMixin {
  clientTimezone?: string | undefined;
  fetch: (path: string, init?: RequestInit) => Promise<any>;
  bookingList: (params: BookingSearchParams) => Promise<BookingSearchResponse>;
  createBooking: (
    booking: CreateBooking,
    userId: string,
    extension?: string | null,
    captchaToken?: string | null
  ) => Promise<Booking>;
  getBookingWithUrl: (url: string) => Promise<Booking>;
  getBookingWithStripeId: (
    stripe_id: string,
    userId: string,
    extension?: string | null
  ) => Promise<Booking>;
  getBooking: (id: number) => Promise<Booking>;
  updateBooking: (
    booking: Partial<Omit<Booking, 'id'>> & { id: number } & { hasTimezone?: boolean | undefined },
    extension?: string | null,
    captchaToken?: string | null,
    patient?: NewPatient | null
  ) => Promise<Booking>;
  deleteBooking: (
    id: number,
    recurrenceId?: number,
    extension?: string | null,
    comments?: string | undefined
  ) => Promise<any>;
  getCharge: (id: number, chargeId: string) => Promise<any>;
  getPaymentLink: (id: number) => Promise<{ url: string }>;
  confirmAssistance: (extension: string, bookingUrl: string) => Promise<{ message: string }>;
  getUserPaymentMethods: () => Promise<{ paymentMethods: string[] }>;
  getUserBookingTags: () => Promise<{ userBookingTags: string[] }>;
  sendPaymentReminder: (bookingId: number) => Promise<any>;
}
export const BookingMixin = {
  async fetch(path: string, init?: RequestInit): Promise<any> {
    throw new Error('Not implemented');
  },
  async bookingList(params: BookingSearchParams): Promise<BookingSearchResponse> {
    const paramsString = buildQueryString(params);
    const bookingRes: BookingSearchResponse = await this.fetch(`/bookings${paramsString}`);

    const timeZone = this.clientTimezone ? this.clientTimezone : DateTime.local().zoneName;
    return {
      ...bookingRes,
      bookings: bookingRes.bookings.map((book) => ({
        ...book,
        startDate: new Date(book.startDate),
      })),
    };
  },
  async createBooking(
    booking: CreateBooking,
    userId: string,
    extension: string | null = null,
    captchaToken: string | null = null
  ): Promise<Booking> {
    const timeZone = this.clientTimezone ? this.clientTimezone : DateTime.local().zoneName;
    const bookingRes = await this.fetch(
      `/bookings${
        userId
          ? `?user_id=${encodeURIComponent(userId)}`
          : extension
            ? `?extension=${encodeURIComponent(extension)}`
            : ''
      }`,
      {
        method: 'post',
        body: JSON.stringify({
          ...booking,
          startDate: new Date(booking.startDate),
          captcha_token: captchaToken,
        }),
      }
    );
    return {
      ...bookingRes,
      startDate: new Date(bookingRes.startDate),
    };
  },
  async getBookingWithUrl(bookingUrl: string): Promise<Booking> {
    const bookingRes = await this.fetch(`/bookings/url/${bookingUrl}`, {
      method: 'get',
    });

    return {
      ...bookingRes,
      startDate: new Date(bookingRes.startDate),
    };
  },
  async getBookingWithStripeId(
    stripe_id: string,
    userId: string,
    extension?: string | null
  ): Promise<Booking> {
    const bookingRes = await this.fetch(
      `/bookings/stripe/${stripe_id}${
        userId
          ? `?user_id=${encodeURIComponent(userId)}`
          : extension
            ? `?extension=${encodeURIComponent(extension)}`
            : ''
      }`,
      {
        method: 'get',
      }
    );
    const timeZone = this.clientTimezone ? this.clientTimezone : DateTime.local().zoneName;
    return {
      ...bookingRes,
      startDate: new Date(bookingRes.startDate),
    };
  },
  async getBooking(id: number): Promise<Booking> {
    const timeZone = this.clientTimezone ? this.clientTimezone : DateTime.local().zoneName;
    const bookingRes = await this.fetch(`/bookings/${id}`, {
      method: 'get',
    });
    return {
      ...bookingRes,
      startDate: new Date(bookingRes.startDate),
    };
  },
  async updateBooking(
    booking: Partial<Omit<Booking, 'id'>> & { id: number } & { hasTimezone?: boolean | undefined },
    extension: string | null = null,
    captchaToken?: string | null,
    patient?: NewPatient | null
  ): Promise<Booking> {
    const timeZone = this.clientTimezone ? this.clientTimezone : DateTime.local().zoneName;
    let addedObj = {};
    if (booking?.startDate) {
      addedObj = {
        startDate: new Date(booking.startDate),
      };
    }
    const bookingRes = await this.fetch(
      `/bookings/${booking.id}${extension ? `?extension=${encodeURIComponent(extension)}` : ''}`,
      {
        method: 'put',
        body: JSON.stringify({
          ...booking,
          ...addedObj,
          captchaToken,
          patient,
        }),
      }
    );
    return {
      ...bookingRes,
      startDate: new Date(bookingRes.startDate),
    };
  },
  async deleteBooking(
    id: number,
    recurrenceId?: number,
    extension: string | null = null,
    comments?: string | undefined
  ): Promise<any> {
    const recurrenceIdQuery = recurrenceId
      ? `recurrence_id=${encodeURIComponent(recurrenceId)}`
      : '';
    const extensionQuery = extension ? `extension=${encodeURIComponent(extension)}` : '';
    const bookingRes = await this.fetch(
      `/bookings/${id}${recurrenceIdQuery || extensionQuery ? '?' : ''}${recurrenceIdQuery}${recurrenceIdQuery && extensionQuery ? `&` : ''}${extensionQuery}`,
      {
        method: 'delete',
        body: JSON.stringify({
          comments,
        }),
      }
    );
    return bookingRes;
  },
  async getCharge(id: number, chargeId: string): Promise<any> {
    const res = await this.fetch(`/bookings/${id}/charges/${chargeId}`);
    return res;
  },
  async getPaymentLink(id: number): Promise<{ url: string }> {
    const res = await this.fetch(`/bookings/${id}/payment-link`);
    return res;
  },
  async confirmAssistance(extension: string, bookingUrl: string): Promise<{ message: string }> {
    const res = await this.fetch('/bookings/confirm', {
      method: 'put',
      body: JSON.stringify({
        extension,
        bookingUrl,
      }),
    });
    return res;
  },
  async getUserPaymentMethods(): Promise<{ paymentMethods: string[] }> {
    const res = await this.fetch('/bookings/payment-methods');
    return res;
  },
  async getUserBookingTags(): Promise<{ userBookingTags: string[] }> {
    const res = await this.fetch('/bookings/user-tags');
    return res;
  },
  async sendPaymentReminder(bookingId: number): Promise<{ message: any }> {
    const res = await this.fetch(`/bookings/${bookingId}/send-payment-reminder`, {
      method: 'post',
    });
    return res;
  },
} as BookingMixin;
